import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    forwardRef,
    HostBinding,
    HostListener,
    Injector,
    Input,
    OnChanges,
    OnInit,
    Type,
    ViewChild
} from '@angular/core';
import {NG_VALUE_ACCESSOR, NgControl} from '@angular/forms';
import * as _ from 'lodash-es';
import {BehaviorSubject, fromEvent, merge, NEVER} from 'rxjs';
import {switchMap, takeUntil, tap} from 'rxjs/operators';
import {TypedSimpleChanges} from '../../common/typed-on-changes';
import {AutocompleteConfig, AutocompleteDirective} from '../autocomplete';
import {ControlValueAccessorWrapperBaseComponent} from '../control-value-accessor-wrapper-base.component';
import {destroy$} from '../destroyable';

const FOCUS_CLASS = '-focus';
const HOVER_CLASS = '-hover';
const HAS_TOGGLE_CONTROL = '-has-toggle-control';
const HAS_CLEAR_CONTROL = '-has-clear-control';
const DISABLED_CLASS = '-disabled';
const OPEN_CLASS = '-open';

@Component({
    selector: 'autocomplete-input',
    templateUrl: './autocomplete-input.component.html',
    styleUrls: ['./autocomplete-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AutocompleteInputComponent),
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AutocompleteInputComponent<T, InnerValue = T>
    extends ControlValueAccessorWrapperBaseComponent<T, InnerValue>
    implements OnInit, OnChanges {

    @Input()
    public autocompleteConfig: AutocompleteConfig;

    @Input()
    public set labelElement(labelElement: HTMLElement) {
        this.labelElement$.next(labelElement);
    }

    /**
     * Displays the control in the right, that allows to open dropdown menu.
     */
    @Input()
    @HostBinding(`class.${HAS_TOGGLE_CONTROL}`)
    public asSelectInput: boolean = true;

    /**
     * Displays the control in the right, that allows to clear the input.
     */
    @Input()
    public showClearBtn: boolean = false;

    @Input()
    public placeholder: string;

    @ViewChild(AutocompleteDirective, {static: true})
    public override innerValueAccessor: AutocompleteDirective;

    @ViewChild(AutocompleteDirective, {read: ElementRef, static: true})
    public innerInputElementRef: ElementRef;

    @HostBinding(`class.${FOCUS_CLASS}`)
    public isFocused: boolean = false;

    @HostBinding(`class.${HOVER_CLASS}`)
    public isLabelHovered: boolean = false;

    @HostBinding(`class.${DISABLED_CLASS}`)
    public isDisabled: boolean = false;

    @HostBinding(`class.${OPEN_CLASS}`)
    public get isListOpen(): boolean {
        return !!this.innerValueAccessor
            && !!this.innerValueAccessor.listComponent;
    }

    public readonly destroy$ = destroy$();

    public ngControl: NgControl;

    @HostBinding(`class.${HAS_CLEAR_CONTROL}`)
    public isClearBtnVisible: boolean = false;

    private readonly labelElement$ = new BehaviorSubject<HTMLElement>(null);

    constructor(
        public elementRef: ElementRef,
        private injector: Injector
    ) {
        super();
    }

    public override writeValue(value: T): void {
        super.writeValue(value);
        this.updateCalculatedFields();
    }

    public override ngOnInit(): void {
        super.ngOnInit();

        this.ngControl = this.injector.get<NgControl>(NgControl as Type<NgControl>, undefined, {self: true});

        this.updateCalculatedFields();
        this.listenToLabelEvents();
    }

    public ngOnChanges(changes: TypedSimpleChanges<AutocompleteInputComponent<T>>): void {
        if (changes.showClearBtn) {
            this.updateCalculatedFields();
        }
    }

    public override setDisabledState(isDisabled: boolean): void {
        this.isDisabled = isDisabled;

        super.setDisabledState(isDisabled);
        this.updateCalculatedFields();
    }

    @HostListener('click')
    public onClick(): void {
        if (!this.isFocused) {
            this.focusInnerInput();
        }
    }

    public onInnerFocus(): void {
        this.isFocused = true;
    }

    public onInnerBlur(): void {
        this.isFocused = false;
    }

    public clear(): void {
        const EMPTY_VALUE: T = null;

        this.writeValue(EMPTY_VALUE);
        this.propagateOnChange(EMPTY_VALUE);
    }

    protected override propagateOnChange(value: T): void {
        super.propagateOnChange(value);
        this.updateCalculatedFields();
    }

    private listenToLabelEvents(): void {
        this.labelElement$.pipe(
            switchMap(element => element
                ? merge(
                    fromEvent(element, 'click').pipe(
                        tap(() => this.focusInnerInput())
                    ),
                    fromEvent(element, 'mouseenter').pipe(
                        tap(() => this.isLabelHovered = true)
                    ),
                    fromEvent(element, 'mouseleave').pipe(
                        tap(() => this.isLabelHovered = false)
                    )
                )
                : NEVER
            ),
            takeUntil(this.destroy$)
        ).subscribe();
    }

    private focusInnerInput(): void {
        (this.innerInputElementRef.nativeElement as HTMLInputElement).focus();
    }

    private updateCalculatedFields(): void {
        this.isClearBtnVisible = !!(
            this.showClearBtn
            && !this.isDisabled
            && this.ngControl && !_.isNil(this.ngControl.value)
        );
    }

}
